import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import Stack from "@mui/material/Stack";
import {FormHelperText} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import {useTranslation} from "react-i18next";
import {CustomTextField} from "./CustomTextField";
import dateInputLanguage from "../hooks/dateInputLanguage";
import Cookies from "js-cookie";
import {
    CustomStyleDatePicker,
    CustomStyleDatePickerRequired,
    CustomStyleFormHelperText
} from "./Styles";

import { validDateEx } from "../Apis/functions";
import {CalenderInputButton} from "./buttons/IconButtons";
import CustomDateTimePickerApp from "./CustomDateTimePicker/CustomDateTimePickerApp";
import {DATE_TIME_PICKER_MODEL} from "./CustomDateRangePicker/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description DatePicker
 * @param String id: Identifier for the DatePicker (required)
 * @param Boolean disabled: Enable/disable writing in the DatePicker (optional)
 * @param String value: Value to display under the DatePicker (required)
 * @param String name: Name for the DatePicker (optional)
 * @param Boolean emptyFields: Indicates if the field is empty (optional)
 * @param Boolean disableFuture: Prevent selection of future dates (optional)
 * @param Boolean disablePast: Prevent selection of past dates (optional)
 * @param String trans: Text for the label (required)
 * @param Boolean isValid: Show legend and input with error when isValid = false (optional)
 * @param String isValidText: Legend for input with error (optional)
 * @param Boolean required: DatePicker is required (optional)
 * @param Array views: Defines list of views to show in the dialog (optional)
 * @param String openTo: Defines the main view (optional)
 * @param Date minDate: Defines the minimum date available for selection (optional)
 * @param String helperText: Text for the legend, displayed under the DatePicker (optional)
 * @param Boolean onlySelecting: If onlySelecting === true, it does not allow entering numbers or arrows in the field (optional)
 * @param Event onError: onError trigger in the DatePicker (optional)
 * @param Event onChange: onChange trigger in the DatePicker (optional)
 * @returns {JSX.Element}
 */
export const CustomDatePicker = ({
    id,
    disabled,
    value,
    name,
    emptyFields,
    disableFuture = false,
    disablePast = false,
    trans,
    isValid = true,
    isValidText = "date_invalid",
    required = false,
    views = ['year', 'month', 'day'],
    openTo = "year",
    minDate = undefined,
    maxDate = undefined,
    helperText,
    onlySelecting = false,
    onError = () => { },
    onChange
}) => {
    const lCode = Cookies.get('i18next') || 'en';
    const { t } = useTranslation();

    const {
        elementRef,
        calculateTop,
        getDatePickerFormat,
        handleKeyDown,
        getPopperStyles
    } = CustomDateTimePickerApp({onlySelecting, type: DATE_TIME_PICKER_MODEL.DATE});

    const dateFormat = getDatePickerFormat();


    return<>
        {
        !disabled ?
            <div ref={elementRef}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={dateInputLanguage(lCode)}
                                      localeText={{ fieldYearPlaceholder: (params) => 'A'.repeat(params.digitAmount) }}>
                    <Stack spacing={3} >
                        <DatePicker  label={t(trans) + (required ? ' *' : '')}
                                inputFormat={dateFormat}
                                     format={ dateFormat}
                                     sx={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? CustomStyleDatePickerRequired : CustomStyleDatePicker(helperText && !emptyFields && isValid)}
                                     value={value ? new Date(value) : null}
                                     onChange={(e)=>{onChange(e, name)}}
                                     name={name}
                                     openTo={openTo}
                                     views={views}
                                     className={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? "input-required" : ""}
                                     minDate={ minDate}
                                     maxDate={ maxDate}
                                     disableFuture={disableFuture}
                                     disablePast={disablePast}
                                     disabled={disabled}
                                     defaultValue={null}
                                     onError={(error) => onError(error)}
                                     onOpen={() => calculateTop(DATE_TIME_PICKER_MODEL.DATE)}
                                     slotProps={{
                                         textField: {
                                             variant: 'outlined',
                                             id: id,
                                             onKeyDown: handleKeyDown,
                                         },
                                         popper: getPopperStyles()
                                     }}
                                     InputProps={{
                                         endAdornment: (
                                             <InputAdornment position="end">
                                                 <CalenderInputButton/>
                                             </InputAdornment>
                                         ),
                                     }}
                        />
                    </Stack>
                </LocalizationProvider>
                {emptyFields && (value === '' || value === null) &&
                    <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t('requiredField')}</FormHelperText>
                }

                {!isValid && value !== '' &&
                    <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t(isValidText)}</FormHelperText>
                }

                { helperText && !emptyFields && isValid &&
                    <FormHelperText sx={CustomStyleFormHelperText}>{t(helperText)}</FormHelperText>
                }
            </div>
            :
            <>
                <CustomTextField id={id}
                                 value={ value ? validDateEx(value) : null}
                                 disabled={disabled}
                                 trans={trans}
                                 type={"text"}
                                 required={required}
                                 InputProps={{
                                     endAdornment: (
                                         <InputAdornment position="end">
                                             <CalenderInputButton/>
                                         </InputAdornment>
                                     ),
                                 }}
                                 helperText={helperText}
                />
            </>
    }
       </>
}
